import UploadStatus from '@/App/Types/enums/UploadStatus';
import FileSizeFormatter from '@/Vendor/inv-file-upload/utils/fileSizeFormatter';

function UploadValidation() {

  var self = this;

  self.maxFileSize = null;
  self.fileNumberLimit = null;
  self.blacklistedFileTypes = null;

  /**
   * Initializes upload validation settings.
   * @param {object} options validation options { maxFileSize, fileNumberLimit, blacklistedFileTypes }
  */
  self.init = function (options) {
    self.maxFileSize = options.maxFileSize;
    self.fileNumberLimit = options.fileNumberLimit;
    self.blacklistedFileTypes = options.blacklistedFileTypes;
  };

  /**
   * Checks if the uploads for a request are in the correct state to allow the request to be submitted.
   * If the validation fails, a resource key for the failure message to display to the user is returned
   * else if everything is OK, null is returned.
   * @param {UploadModel[]} uploads Array of UploadModel for each file that already exists
   * @param {bool} areUploadsOptional Flag indicates whether at least one uploaded file is required
   * @return {string}
   */
  self.getValidationResourceKey = function (uploads, areUploadsOptional = false) {

    if (uploads.length === 0) {
      const resourceKey = areUploadsOptional === false ? 'NoFilesUploaded' : null;
      return resourceKey;
    }

    var statuses = uploads.map(function (upload) {
      return upload.statusName();
    });


    var containsAuthorising = statuses.indexOf(UploadStatus.authorising) > -1;
    var containsPending = statuses.indexOf(UploadStatus.pending) > -1;
    var containsUploading = statuses.indexOf(UploadStatus.uploading) > -1;
    var containsFailed = statuses.indexOf(UploadStatus.failed) > -1;
    var containsFailedAuthorization = statuses.indexOf(UploadStatus.failedAuthorisation) > -1;

    if (containsAuthorising || containsPending || containsUploading) {
      return 'UploadsStillInProgress';
    }

    if (containsFailed || containsFailedAuthorization) {
      return 'FailedUploadsExist';
    }

    return null;
  };

  /**
   * Check if any of the files attempting to be added have the same filenames as those that already exist.
   * @param {FileList} filesToAdd Native browser FileList object
   * @param {UploadModel[]} uploads Array of UploadModel for each file that already exists
   * @return {boolean}
   */
  function doAnyFileNamesAlreadyExist(filesToAdd, uploads) {
    var fileNamesToAdd = filesToAdd.map(function (file) {
      return file.name;
    });

    var existingFileNames = uploads.map(function (upload) {
      return upload.fileName;
    });

    for (var i = 0; i < fileNamesToAdd.length; i++) {

      if (existingFileNames.indexOf(fileNamesToAdd[i]) !== -1) {
        return true;
      }
    }

    return false;
  }

  /**
   * @param {FileList} fileList Native browser FileList object
   * @return {Array}
   */
  function convertFileListToArray(fileList) {

    var i;
    var files = [];

    for (i = 0; i < fileList.length; i++) {
      files.push(fileList[i]);
    }

    return files;
  }

  /**
   * Check if any of the files attempting to be added excess alloved size.
   * @param {FileList} filesToAdd Native browser FileList object
   * @param {number} maxFileSize
   * @return {boolean}
   */
  function doAnyFileExceedAllovedSize(filesToAdd, maxFileSize) {
    return filesToAdd.some(function (file) {
      return file.size >= maxFileSize;
    });
  }

  /**
   * Check if number of files attempting to be added is more than {uploadMaxFilesCount} items.
   * @param {FileList} filesToAdd Native browser FileList object
   * @param {UploadModel[]} uploads Array of UploadModel for each file that already exists
   * @param {number} fileNumberLimit
   * @return {boolean}
   */
  function doFilesMetFileNumberLimit(filesToAdd, uploads, fileNumberLimit) {
    return filesToAdd.length + uploads.length > fileNumberLimit;
  }

  /**
   * Check if any of the files attempting to be added has blacklisted file type.
   * @param {FileList} filesToAdd Native browser FileList object
   * @return {boolean}
   */
  function doAnyFilesHaveBlacklistedFileType(filesToAdd) {
    return filesToAdd.some(function (file) {
      return fileHasBlacklistedFileType(file);
    });
  }

  function fileHasBlacklistedFileType(file) {
    var match = file.name.match(/\.\w+$/) || [];

    if (match.length === 0) {
      return true;
    }

    var currentFileExt = match[0];

    return self.blacklistedFileTypes.some(function (ext) {
      return ext === currentFileExt;
    });
  }

  /**
   * Check if any of the files attempting to be added has no content.
   * @param {FileList} filesToAdd Native browser FileList object
   * @return {boolean}
   */
  function doAnyFilesHaveNoContent(filesToAdd) {
    return filesToAdd.some(function (file) {
      return file.size === 0;
    });
  }

  self.getValidationResourceKeyForNewUploads = function (fileList, uploads) {

    var filesToAdd = convertFileListToArray(fileList);

    switch (true) {
      case doAnyFileNamesAlreadyExist(filesToAdd, uploads):
        return { key: 'UploadAlreadyExists' };
      case doAnyFileExceedAllovedSize(filesToAdd, self.maxFileSize):
        return {
          key: 'MaxFileSizeExcess',
          params: { maxFileSize: FileSizeFormatter.getFormattedFileSize(self.maxFileSize) }
        };
      case doFilesMetFileNumberLimit(filesToAdd, uploads, self.fileNumberLimit):
        return {
          key: 'MetFileNumberLimit',
          params: { fileNumberLimit: self.fileNumberLimit }
        };
      case doAnyFilesHaveBlacklistedFileType(filesToAdd):
        return { key: 'BlacklistedFileType' };
      case doAnyFilesHaveNoContent(filesToAdd):
        return { key: 'FilesHasNoContent' };
      default:
        return '';
    }
  };
}

export default new UploadValidation();
